import { PaginationState } from "@tanstack/react-table";
import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router";

/**
 * Custom hook to manage pagination with URL parameters without breaking browser back button
 * Returns a pagination change handler that only updates URL when user actually interacts with pagination
 */
export const usePaginationWithParams = (
  initialPagination: PaginationState,
  setPagination: (
    pagination: PaginationState | ((prev: PaginationState) => PaginationState),
  ) => void,
) => {
  const [, setSearchParams] = useSearchParams();
  const isInitializedRef = useRef(false);

  const handlePaginationChange = useCallback(
    (
      newPagination:
        | PaginationState
        | ((prev: PaginationState) => PaginationState),
    ) => {
      setPagination(newPagination);

      // Only update URL params after initial load when user actually interacts
      if (isInitializedRef.current) {
        const finalPagination =
          typeof newPagination === "function"
            ? newPagination(initialPagination)
            : newPagination;

        setSearchParams((prev) => {
          if (finalPagination.pageIndex === 0) {
            // Remove page parameter when on first page to avoid breaking back button
            prev.delete("page");
          } else {
            // Only set page parameter when not on first page
            prev.set("page", finalPagination.pageIndex.toString());
          }
          return prev;
        });
      } else {
        // Mark as initialized after first call
        isInitializedRef.current = true;
      }
    },
    [setPagination, setSearchParams, initialPagination],
  );

  return handlePaginationChange;
};

/**
 * Legacy hook for manual updates - kept for backward compatibility
 */
export const usePaginationParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastPageRef = useRef<number | null>(null);

  const updatePageParam = useCallback(
    (pageIndex: number) => {
      // Prevent unnecessary updates if the page hasn't actually changed
      if (lastPageRef.current === pageIndex) {
        return;
      }

      const currentPage = searchParams.get("page");
      const currentPageNumber = currentPage ? parseInt(currentPage, 10) : 0;

      // Only update if the page actually changed from what's in the URL
      if (pageIndex === currentPageNumber) {
        lastPageRef.current = pageIndex;
        return;
      }

      lastPageRef.current = pageIndex;

      setSearchParams((prev) => {
        if (pageIndex === 0) {
          // Remove page parameter when on first page to avoid breaking back button
          prev.delete("page");
        } else {
          // Only set page parameter when not on first page
          prev.set("page", pageIndex.toString());
        }
        return prev;
      });
    },
    [searchParams, setSearchParams],
  );

  return { updatePageParam };
};
